import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, Shield, Crown } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/kit";

const ROLE_STYLE = { manager: "text-volt", support: "text-[#f59e0b]", moderator: "text-zinc-300" };

export default function Staff() {
  const { guildId, guild } = useGuild();
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ email: "", role: "moderator" });
  const canManage = guild && (String(guild.owner_id) === String(user?.id) || user?.role === "admin");

  const load = () => api.get(`/guilds/${guildId}/staff`).then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, [guildId]);

  const add = async () => {
    if (!form.email) return toast.error("Enter the member's email");
    try { await api.post(`/guilds/${guildId}/staff`, form); toast.success("Staff member added"); setForm({ email: "", role: "moderator" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/guilds/${guildId}/staff/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <PageHead title="Staff & Permissions" desc="Invite co-moderators to help manage this server's dashboard." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {canManage && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4 h-fit">
            <h3 className="font-head font-semibold flex items-center gap-2"><UserPlus className="w-4 h-4 text-volt" /> Invite staff</h3>
            <div><label className="text-xs uppercase tracking-wider text-zinc-500">Member email</label>
              <input data-testid="staff-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="member@email.com" className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" /></div>
            <div><label className="text-xs uppercase tracking-wider text-zinc-500">Role</label>
              <select data-testid="staff-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt">
                <option value="moderator">Moderator</option><option value="support">Support</option><option value="manager">Manager</option>
              </select></div>
            <button data-testid="staff-add" onClick={add} className="w-full bg-volt text-volt-ink font-semibold rounded-md py-2 hover:bg-volt-hover flex items-center justify-center gap-2"><UserPlus className="w-4 h-4" /> Add member</button>
            <p className="text-xs text-zinc-600">The person must already have a Crank.live account.</p>
          </div>
        )}
        <div className={`bg-zinc-900 border border-zinc-800 rounded-xl p-6 ${canManage ? "lg:col-span-2" : "lg:col-span-3"}`}>
          <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><Users className="w-4 h-4 text-volt" /> Team</h3>
          <div className="flex items-center gap-3 py-2 border-b border-zinc-800">
            <Crown className="w-5 h-5 text-volt" />
            <div className="flex-1"><div className="font-medium">Server Owner</div><div className="text-xs text-zinc-500">Full access</div></div>
            <span className="text-xs text-volt">owner</span>
          </div>
          <div className="space-y-2 mt-2">
            {staff.map((s) => (
              <div key={s.id} data-testid="staff-row" className="flex items-center gap-3 py-2">
                <Shield className={`w-5 h-5 ${ROLE_STYLE[s.role] || "text-zinc-400"}`} />
                <div className="flex-1"><div className="font-medium">{s.name}</div><div className="text-xs text-zinc-500">{s.email}</div></div>
                <span className={`text-xs capitalize ${ROLE_STYLE[s.role] || "text-zinc-400"}`}>{s.role}</span>
                {canManage && <button data-testid="staff-remove" onClick={() => remove(s.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
            {staff.length === 0 && <p className="text-zinc-600 text-sm py-2">No staff yet — just the owner.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
